// v0.4.45 — Single amendment row + audit drawer.
import React, { useState } from "react";
import { amendmentAudit, appendAmendmentNote } from "../lib/api";
import { AmendmentResolveModal } from "./AmendmentResolveModal";
import { AmendmentSubmissionModal } from "./AmendmentSubmissionModal";
import { labelFor } from "../lib/enumLabels";
import { PresetReasonSelect } from "./PresetReasonSelect";
import { AMENDMENT_NOTES } from "../lib/reviewReasonPresets";
import { useTranslation } from "react-i18next";

const STATUS_COLOURS = {
  OPEN: "bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  SUBMITTED: "bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-700",
  RESOLVED: "bg-slate-700/40 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-700",
};

export const AmendmentRow = ({ amendment, onChanged }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [audit, setAudit] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [notePreset, setNotePreset] = useState("");
  // A2-571 — a phone or letter reference is an identifier, not a
  // sentence: letters, digits, spaces and / . - only, 40 at most.
  const [noteRef, setNoteRef] = useState("");

  const isOpen = amendment.status === "OPEN";
  const isSubmitted = amendment.status === "SUBMITTED";

  const loadAudit = async () => {
    try {
      const res = await amendmentAudit(amendment.id);
      setAudit(res?.audit || []);
    } catch {
      setAudit([]);
    }
  };

  const toggleDrawer = async () => {
    const next = !open;
    setOpen(next);
    if (next && audit === null) await loadAudit();
  };

  const submitNote = async () => {
    const ref = noteRef.trim();
    const txt = noteDraft.trim() + (ref ? ` Reference: ${ref}.` : "");
    if (!noteDraft.trim()) return;
    try {
      await appendAmendmentNote(amendment.id, txt);
      setNoteDraft("");
      setNotePreset("");
      setNoteRef("");
      onChanged?.();
      await loadAudit();
    } catch {
      // surfaced via the next refresh
    }
  };

  return (
    <div
      data-testid={`amendment-row-${amendment.id}`}
      className="border border-slate-200 dark:border-slate-700 rounded-2xl bg-slate-50 dark:bg-slate-950 mb-3"
    >
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              data-testid={`amendment-status-${amendment.id}`}
              className={`text-[10px] tracking-normal px-2 py-0.5 rounded-full border ${STATUS_COLOURS[amendment.status] || ""}`}
            >
              {labelFor("amendment_status", amendment.status)}
            </span>
            {amendment.overdue && (
              <span
                data-testid={`amendment-overdue-${amendment.id}`}
                className="text-[10px] tracking-normal px-2 py-0.5 rounded-full border bg-rose-500/15 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800"
              >
                {t("ui.amendments.overdue_badge", { defaultValue: "Overdue · 4y bar" })}
              </span>
            )}
            <span className="text-[10px] tracking-normal text-slate-500 dark:text-slate-400">
              {labelFor("trigger_kind", amendment.trigger_kind)}
            </span>
          </div>
          <div className="text-base text-slate-900 dark:text-slate-50">
            {amendment.country}
            {amendment.category && (
              <> · <span className="tabular-nums text-slate-500 dark:text-slate-400">{amendment.category}</span></>
            )}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate">
            {t("ui.amendments.row_opened", { at: amendment.opened_at?.slice(0, 19) ?? "", by: amendment.opened_by ?? "", n: amendment.affected_filing_refs?.length || 0, defaultValue: `opened ${amendment.opened_at?.slice(0, 19) ?? ""} by ${amendment.opened_by ?? ""} · ${amendment.affected_filing_refs?.length || 0} filing(s)` })}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {isOpen && (
            <button
              data-testid={`amendment-record-submission-${amendment.id}`}
              data-resolution-class="recordkeeping"
              data-resolution-endpoint="navigation"
              onClick={() => setShowSubmit(true)}
              className="border border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:bg-teal-900/40 dark:hover:bg-teal-900/40 px-3 py-1.5 rounded-full text-xs"
            >
              {t("ui.amendments.record_submission", { defaultValue: "Record submission" })}
            </button>
          )}
          {(isOpen || isSubmitted) && (
            <button
              data-testid={`amendment-resolve-${amendment.id}`}
              data-resolution-class="self-resolve"
              data-resolution-endpoint="navigation"
              onClick={() => setShowResolve(true)}
              className="bg-amber-600 hover:bg-amber-400 text-white font-semibold px-3 py-1.5 rounded-full text-xs"
            >
              {t("ui.amendments.resolve", { defaultValue: "Resolve" })}
            </button>
          )}
          <button
            data-testid={`amendment-toggle-drawer-${amendment.id}`}
            onClick={toggleDrawer}
            className="text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 text-xs underline"
          >
            {open ? t("ui.amendments.hide_details", { defaultValue: "Hide details" }) : t("ui.amendments.show_details", { defaultValue: "Show details" })}
          </button>
        </div>
      </div>

      {open && (
        <div className="px-5 pb-4 border-t border-slate-200 dark:border-slate-700 pt-3">
          <div className="text-[10px] tracking-normal text-slate-500 dark:text-slate-400 mb-1">
            {t("ui.amendments.affected_filings", { defaultValue: "Affected filings" })}
          </div>
          <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-0.5 mb-3">
            {(amendment.affected_filing_refs || []).map((r, i) => (
              <li key={i}>· {r || "—"}</li>
            ))}
          </ul>

          <div className="text-[10px] tracking-normal text-slate-500 dark:text-slate-400 mb-1">
            {t("ui.amendments.audit_trail", { defaultValue: "Audit trail" })}
          </div>
          {audit === null ? (
            <div className="text-xs text-slate-500 dark:text-slate-400">{t("ui.common.loading", { defaultValue: "loading…" })}</div>
          ) : audit.length === 0 ? (
            <div className="text-xs text-slate-500 dark:text-slate-400">{t("ui.amendments.no_audit_entries", { defaultValue: "No audit entries." })}</div>
          ) : (
            <ul className="text-xs space-y-1 mb-3">
              {audit.map((a) => (
                <li key={a.id} className="text-slate-600 dark:text-slate-300">
                  <span className="text-slate-500 dark:text-slate-400 tabular-nums">{a.timestamp?.slice(0, 19)}</span>
                  {" · "}
                  <span className="text-slate-800 dark:text-slate-100">{a.event}</span>
                  {" · "}
                  <span className="text-slate-500 dark:text-slate-400">{a.operator_email}</span>
                </li>
              ))}
            </ul>
          )}

          {amendment.status !== "RESOLVED" && (
            <div className="flex gap-2 items-start">
              {/* v0.6.12 A5-27 — the common notes are picked. A2-571 —
                  no typed "Other"; a phone or letter reference goes in
                  the reference box, which takes an identifier only. */}
              <div className="flex-1 space-y-2">
              <PresetReasonSelect
                testid={`amendment-note-preset-${amendment.id}`}
                presets={AMENDMENT_NOTES}
                preset={notePreset}
                onPick={(p, v) => { setNotePreset(p); setNoteDraft(v); }}
              />
              <label className="block text-xs text-slate-600 dark:text-slate-300">
                {t("ui.amendments.note_ref_label", { defaultValue: "Reference (optional) — e.g. a call or letter reference" })}
                <input
                  type="text"
                  data-testid={`amendment-note-ref-${amendment.id}`}
                  value={noteRef}
                  maxLength={40}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => setNoteRef(e.target.value.replace(/[^A-Za-z0-9 ./-]/g, ""))}
                  placeholder="HMRC-12345…"
                  className="mt-1 w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-900 dark:text-slate-50 text-base sm:text-xs focus:border-teal-500 dark:focus:border-teal-400 focus:outline-none"
                />
              </label>
              </div>
              <button
                data-testid={`amendment-note-submit-${amendment.id}`}
                onClick={submitNote}
                disabled={!noteDraft.trim()}
                className={`px-3 py-2 rounded-full text-xs ${
                  noteDraft.trim()
                    ? "bg-slate-100 dark:bg-slate-800 hover:bg-slate-700 text-slate-900 dark:text-slate-50"
                    : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 cursor-not-allowed"
                }`}
              >
                {t("ui.amendments.note_submit", { defaultValue: "Note" })}
              </button>
            </div>
          )}
        </div>
      )}

      {showResolve && (
        <AmendmentResolveModal
          amendment={amendment}
          onClose={() => setShowResolve(false)}
          onResolved={() => { onChanged?.(); }}
        />
      )}
      {showSubmit && (
        <AmendmentSubmissionModal
          amendment={amendment}
          onClose={() => setShowSubmit(false)}
          onSubmitted={() => { onChanged?.(); }}
        />
      )}
    </div>
  );
};

export default AmendmentRow;
